import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiService } from './services/api';
import { knowledgeService } from './services/api'; 
import { SearchProvider } from './contexts/SearchContext';
import { CISearchProvider } from './contexts/CISearchContext';
import { JournalSearchProvider } from './contexts/JournalSearchContext';
import { SpeechRecognitionProvider } from './contexts/SpeechRecognitionContext';
import JoinWaitlist from './components/Admin/JoinWaitlist';

// Component imports
import Header from './components/Header/Header';
import MobileBanner from './components/MobileBanner/MobileBanner';
import Sidebar from './components/Sidebar/Sidebar';
import Footer from './components/Footer/Footer';
import Login from './components/Login/Login';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import VerifyMagicLink from './components/VerifyMagicLink/VerifyMagicLink';
// Registration is intentionally routed to the waitlist during closed alpha;
// Register is kept wired for when public registration is restored.
// eslint-disable-next-line no-unused-vars
import Register from './components/Register/Register';
import VerifyEmail from './components/VerifyEmail/VerifyEmail';
import MyAccount from './components/MyAccount/MyAccount';
import MyKnowledgeEntries from './components/MyKnowledgeEntries/MyKnowledgeEntries';
import Journal from './components/Journal/Journal';
import UnexploredLinks from './components/UnexploredLinks/UnexploredLinks';
import Admin from './components/Admin/Admin';
import AIExplorerPage from './components/AIExplorer/AIExplorerPage';
import CollectiveIntelligence from './components/CollectiveIntelligence/CollectiveIntelligence';
// LandingSky replaced the previous marketing splash (components/Landing/), which
// was deleted on October 2, 2026 after sign-off. Recover it from git history if
// anything there is ever wanted back.
import LandingSky from './components/LandingSky/LandingSky';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './components/TermsOfService/TermsOfService';
import MissionVision from './components/MissionVision/MissionVision';
import ScrollToTop from './components/common/ScrollToTop/ScrollToTop';
import AdminRoute from './components/common/AdminRoute';
import PhilPage from './components/Phil/PhilPage';

const AuthenticatedApp = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // =========================================
  // State Management
  // =========================================
  const [state, setState] = useState({
    searchResults: null,
    tagsList: [],
    sourcesList: [],
    selectedTags: [],
    selectedSources: [],         // Track selected sources for filtering
    sourceCorrection: null,
    sourcesWithCounts: [],       // Store sources with their document counts
    unidentifiedSources: [],
    isSourceCorrectionOpen: false,
  });

  // =========================================
  // Search Handlers
  // =========================================
  const handleSearchResults = useCallback(async (query) => {
    if (!query) {
      setState(prev => ({ ...prev, searchResults: null }));
      return;
    }

    try {
      const results = await knowledgeService.search(query);
      setState(prev => ({ ...prev, searchResults: results.data }));
    } catch (error) {
      console.error('Search error:', error);
      setState(prev => ({ ...prev, searchResults: [] }));
    }
  }, []);

  // =========================================
  // Source Selection Handlers
  // =========================================
  const handleSourceSelection = useCallback((source) => {
    console.debug('Source selection started:', {
      source,
      currentSources: state.selectedSources
    });

    const newSelection = state.selectedSources.includes(source)
      ? state.selectedSources.filter(s => s !== source)
      : [...state.selectedSources, source];

    apiService.knowledge.getEntries(1, newSelection.length ? { sources: newSelection } : {})
      .then(results => {
        console.debug('Updating App state with:', {
          newSelection,
          resultsReceived: !!results,
          entriesCount: results?.entries?.length
        });
      
        setState(prev => ({
          ...prev,
          selectedSources: newSelection,
          searchResults: results
        }));
      });
  }, [state.selectedSources]);

  // =========================================
  // Source Correction Handler
  // =========================================
  const handleSourceCorrection = useCallback((correction) => {
    console.debug('Source correction submitted:', correction);
    setState(prev => ({ ...prev, sourceCorrection: correction }));
  }, []);

  // Show loading only during initialization
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="app">
      <MobileBanner />
      <Header user={user} />
      <div className={`main-container ${!isAuthenticated ? 'login-container' : ''}`}>
        {isAuthenticated && (
          // ===== MODIFICATION #4: Add Timeline Props to Sidebar =====
          <Sidebar
            user={user}
            onSearchResults={handleSearchResults}
            tagsList={state.tagsList}
            selectedTags={state.selectedTags}
            selectedSources={state.selectedSources}
            onSourceSelection={handleSourceSelection}
            onSourceCorrection={handleSourceCorrection}
          />
        )}
        <div className="content-area">
          <Routes>
            {/* Public routes */}
              {/* "/" and "/landing" are NOT here any more. The landing page is
                  full-bleed and must render outside this shell, so both routes
                  moved to the top-level <Routes> in App, beside /terms and
                  /privacy — see LandingSkyRoute. A route left here would be
                  unreachable: the top-level match wins before AuthenticatedApp
                  mounts. The authenticated redirect at "/" is unchanged; it
                  moved with the route. */}
              {/* "/login", "/join-waitlist" and "/forgot-password" are NOT here
                  any more. All three moved onto the sky in October 2026 and are
                  full-bleed, so they are declared in the top-level <Routes> in
                  App beside "/" — see SkyRoute, which carries the
                  authenticated redirect each of them used to hold here. A route
                  left here would be unreachable: the top-level match wins before
                  AuthenticatedApp mounts.

                  "/register" stays, because it renders no page of its own — it
                  is a bare redirect to the waitlist. */}
              <Route
                path="/register"
                element={<Navigate to="/join-waitlist" replace />}
              />
              <Route
                path="/verify-magic-link/:token"
                element={<VerifyMagicLink />}
              />
              <Route
                path="/verify-email/:token"
                element={<VerifyEmail />}
              />
              {/* /privacy and /terms are NOT here — they are matched by the
                  top-level <Routes> in App below and render standalone, outside
                  this shell. See StandaloneDocument. */}
              <Route path="/mission-vision" element={<MissionVision />} />

            {/* Protected routes */}
            <Route
              path="/account"
              element={isAuthenticated ? <MyAccount /> : <Navigate to="/login" />}
            />
            <Route
              path="/my-knowledge-entries"
              element={
                isAuthenticated ? (
                  <MyKnowledgeEntries
                    searchResults={state.searchResults}
                    selectedTags={state.selectedTags}
                    selectedSources={state.selectedSources}
                    sourceCorrection={state.sourceCorrection}
                    unidentifiedSources={state.unidentifiedSources}
                    onSourceCorrection={handleSourceCorrection}
                  />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/journal"
              element={isAuthenticated ? <Journal /> : <Navigate to="/login" />}
            />

            {/* PHIL: Restricted to admins during redesign (Jan 2026) */}
            {/* Phil 2.0 will integrate with AI Explorer synthesis */}
            <Route
              path="/phil"
              element={
                isAuthenticated ? (
                  <AdminRoute>
                    <PhilPage />
                  </AdminRoute>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/ai-explorer"
              element={isAuthenticated ? <AIExplorerPage /> : <Navigate to="/login" />}
            />

            <Route
              path="/collective-intelligence"
              element={isAuthenticated ? <CollectiveIntelligence /> : <Navigate to="/login" />}
            />

            <Route
              path="/unexplored-links"
              element={isAuthenticated ? <UnexploredLinks /> : <Navigate to="/login" />}
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={isAuthenticated && user?.isAdmin ? <Admin /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/waitlist"
              element={isAuthenticated && user?.isAdmin ? <Admin section="waitlist" /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/users"
              element={isAuthenticated && user?.isAdmin ? <Admin section="users" /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/organizations"
              element={isAuthenticated && user?.isAdmin ? <Admin section="organizations" /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/costs"
              element={isAuthenticated && user?.isAdmin ? <Admin section="costs" /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin/growth"
              element={isAuthenticated && user?.isAdmin ? <Admin section="growth" /> : <Navigate to="/login" />}
            />

            {/* Fallback route */}
            <Route
              path="*"
              element={<Navigate to={isAuthenticated ? "/my-knowledge-entries" : "/"} />}
            />
          </Routes>
        </div>
      </div>
      {isAuthenticated && <Footer />}  {/* ← Only show when logged in */}
    </div>
  );
};

/**
 * Chrome for the public legal documents (/terms, /privacy).
 *
 * These render OUTSIDE AuthenticatedApp: no Sidebar, no Footer, and none of the
 * .main-container / .content-area wrappers. Two reasons this is a wrapper rather
 * than a conditional inside AuthenticatedApp:
 *
 *   1. Auth independence. AuthenticatedApp returns a "Loading..." screen while
 *      isLoading is true, so a signed-in user opening /terms in a new tab would
 *      see a spinner before the document. Outside that component there is no
 *      auth gate at all — the page is identical whether or not anyone is logged in.
 *   2. Layout. Inside the shell these documents were stacking three top offsets
 *      (.main-container 60px + .content-area margin-top 60px + the document's own
 *      60px) and scrolling inside a nested .content-area viewport. Rendered
 *      directly, the document's own 60px padding-top — commented in its CSS as
 *      "Account for fixed header" — clears the fixed Header exactly as its
 *      author intended, and the page scrolls on the body.
 *
 * MobileBanner and Header are kept deliberately. Both documents' CSS reserves
 * 60px for the fixed header, and the sibling marketing pages (Landing,
 * MissionVision) render under the same header — dropping it here would leave a
 * bare 60px gap and make these two pages inconsistent with the rest of the
 * public site.
 */
const StandaloneDocument = ({ children }) => (
  <div className="standalone-document">
    <MobileBanner />
    <Header />
    {children}
  </div>
);

/**
 * LandingSkyRoute
 *
 * "/" for everyone: the landing sky for visitors, a redirect for signed-in users.
 *
 * This exists because the landing page moved OUT of AuthenticatedApp (it is
 * full-bleed and cannot render inside the shell's sidebar/nav layout), which
 * means the authenticated redirect that used to live on the inner "/" route has
 * to live somewhere too. The behaviour is carried over unchanged:
 *
 *   - Signed-in users never see the marketing splash. They go where the "*"
 *     fallback already sends them. A redirect rather than a conditional render,
 *     so the URL reflects where the user actually is.
 *
 *   - `replace` is required, not cosmetic — without it "/" stays in history, and
 *     Back from /my-knowledge-entries returns to "/", which redirects forward
 *     again, leaving the Back button dead.
 *
 * The isLoading guard is what AuthenticatedApp's own Loading screen used to
 * provide for free: without it, isAuthenticated is false during the auth check
 * and a signed-in user would see the landing page flash before being redirected.
 * It paints the sky's darkest colour rather than "Loading...", so for the common
 * case — a visitor — the first paint is already the right colour and the sky
 * simply resolves onto it.
 */
const LandingSkyRoute = () => <SkyRoute element={<LandingSky />} />;

/**
 * SkyRoute
 *
 * The guard every full-bleed sky page shares: show the page to visitors, send
 * signed-in users to their knowledge base, and paint sky-coloured rather than
 * white while the auth check is in flight.
 *
 * Generalised from LandingSkyRoute on October 2026 when /login, /join-waitlist
 * and /forgot-password moved onto the sky. All three carried exactly this
 * ternary inside AuthenticatedApp before the move; the only addition is the
 * isLoading branch, which the shell's own "Loading..." screen used to provide
 * for them and which top-level routes do not get for free.
 *
 * WHY THESE ROUTES ARE TOP-LEVEL AND NOT IN AuthenticatedApp: a sky page is
 * full-bleed. Inside the shell it would render within `.content-area` (60px top
 * margin, 40px bottom, 12px padding) underneath a `position: fixed` light
 * Header at z-index 1000 — which would sit on top of the sky, since the sky
 * keeps itself in the 0-3 band. The shell's own light lockup and the 60px it
 * reserves would both cut into the composition.
 */
const SkyRoute = ({ element }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="landing-sky-boot" />;
  }

  if (isAuthenticated) {
    return <Navigate to="/my-knowledge-entries" replace />;
  }

  return element;
};

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <SpeechRecognitionProvider>
          <SearchProvider>
            <CISearchProvider>
              <JournalSearchProvider>
                {/* The legal documents and the landing page are split out at the
                    top level so the authenticated shell never wraps them.
                    Everything else — including the "*" fallback — stays inside
                    AuthenticatedApp, whose own <Routes> matches the full path
                    because this parent route's path ends in "*". */}
                <Routes>
                  {/* The landing sky is full-bleed: it paints its own nav, its
                      own footer, and a fixed backdrop behind the whole viewport.
                      Unlike /terms and /privacy below it is NOT wrapped in
                      StandaloneDocument — the shared Header's light lockup and
                      the 60px it reserves would both cut into the sky. */}
                  <Route path="/" element={<LandingSkyRoute />} />
                  {/* A direct, non-redirecting way to see the page, including
                      while signed in. Mirrors the old "/landing" route. */}
                  <Route path="/landing" element={<LandingSky />} />
                  {/* The three public form pages, on the same sky. Each carries
                      the authenticated redirect it had inside AuthenticatedApp.
                      Like "/" and unlike /terms and /privacy, they are NOT
                      wrapped in StandaloneDocument — see SkyRoute. */}
                  <Route path="/login" element={<SkyRoute element={<Login />} />} />
                  <Route
                    path="/join-waitlist"
                    element={<SkyRoute element={<JoinWaitlist />} />}
                  />
                  <Route
                    path="/forgot-password"
                    element={<SkyRoute element={<ForgotPassword />} />}
                  />
                  <Route
                    path="/terms"
                    element={
                      <StandaloneDocument>
                        <TermsOfService />
                      </StandaloneDocument>
                    }
                  />
                  <Route
                    path="/privacy"
                    element={
                      <StandaloneDocument>
                        <PrivacyPolicy />
                      </StandaloneDocument>
                    }
                  />
                  <Route path="*" element={<AuthenticatedApp />} />
                </Routes>
              </JournalSearchProvider>
            </CISearchProvider>
          </SearchProvider>
        </SpeechRecognitionProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
